import React, { useMemo, useState } from 'react'
import { useTheme, Typography, Box, Tooltip } from '@mui/material';
import { colord, extend } from "colord";
import mixPlugin from "colord/plugins/mix";
import { KeywordDisplayType, TimeOfDayOfWeekSettingsStateType } from './TimeOfDayOfWeekSettings';
import { getSingleActiveWidgetRequestByName, Widget } from 'system/widget/Widget';
import WidgetTitle from 'components/widgetParts/WidgetTitle';
import WidgetLayout from 'components/widgetParts/WidgetLayout';
import TimeOfDayOfWeekTooltip from './TimeOfDayOfWeekTooltip';
import { useTranslation } from 'react-i18next';

extend([mixPlugin]);

interface TimeOfDayOfWeekContentProps {
  settingsState: TimeOfDayOfWeekSettingsStateType;
  apiResponseBody?: { [key: string]: any };

  fieldLabel?: string;
  bottomFieldLabel?: string;

  subAggOpLabel?: string;

  topField?: string;
  queryDisplayTitle?: string;
  widget?: Widget;
}

interface TimeOfDayOfWeekRow {
  _count: number;
  _key: number;
  _label: string;
  subagg?: {
    _field_id: string;
    _field_label: string;
    _buckets: Array<{
      _key: string;
      _count: number;
      _label: string;
    }>;
  } | number;
}

const hoursOfDay = [0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23];

export interface TimeOfDayOfWeekContentRow {
  original: TimeOfDayOfWeekRow,
  value: number,
  topValue: number,
  bottomValue: number,
  dayOfWeekLabel: string,
  timeOfDayLabel: string,
  isPercentage: boolean,
  isNet: boolean
}

export function getTimeOfDayOfWeekRowContent(row: TimeOfDayOfWeekRow, settingsState: TimeOfDayOfWeekSettingsStateType, topField?: string) {
  let value = row._count;

  const hasSubAggs = row.subagg !== undefined;
  const isPercentage = settingsState.keywordDisplayType === KeywordDisplayType.PERCENTAGE;
  const isNet = settingsState.keywordDisplayType === KeywordDisplayType.NET && topField;

  let topValue = 0,
      bottomValue = 0;

  if (hasSubAggs) {
    if (typeof row.subagg === "number") {
      value = row.subagg;
    } else if (typeof row.subagg === "object") {
      const subBuckets = row?.subagg?._buckets;
      if (isPercentage) {
        const val = subBuckets?.[0]?._count || 0;
        value = (val / value) * 100;
      } else if (isNet) {
        let topBucket = subBuckets?.[0];
        let bottomBucket = subBuckets?.[1];
        if (topBucket?._key !== topField) {
          // Returned in reverse order, swap the buckets
          const temp = topBucket;
          topBucket = bottomBucket;
          bottomBucket = temp;
        }
        topValue = topBucket?._count || 0;
        bottomValue = bottomBucket?._count || 0;
        value = (topValue / (topValue + bottomValue)) * 100;
      } else {
        value = subBuckets?.[0]?._count || 0;
      }
    }
  }

  return {
    original: row,
    value: value || 0,
    topValue: topValue || 0,
    bottomValue: bottomValue || 0,
    dayOfWeekLabel: row._label.split(" ")[0] || "",
    timeOfDayLabel: row._label.split(" ")[1] || "",
    isPercentage: isPercentage,
    isNet: isNet
  }
}

export default function TimeOfDayOfWeekContent(props: TimeOfDayOfWeekContentProps): React.JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const { settingsState, apiResponseBody, topField, queryDisplayTitle, widget } = props;

  const [hoveredIndex, setHoveredIndex] = useState<number | undefined>(undefined);

  let displayTitle = props.fieldLabel;

  const firstBucket = apiResponseBody?.time_of_day_of_week?._buckets?.[0];
  const dateFieldLabel = apiResponseBody?.time_of_day_of_week?._field_label;
  const firstBucketSubaggs = firstBucket && firstBucket.subagg;

  let topFieldValueLabel = undefined,
      bottomFieldValueLabel = undefined;

  if (firstBucketSubaggs && firstBucket?.subagg?._buckets?.length) {
    let topSubBucket = firstBucket.subagg._buckets[0],
        bottomSubBucket = firstBucket.subagg._buckets[1];

    // The order of filtered values returned by the API is not guaranteed,
    // so we check which is which and swap if necessary
    if (bottomSubBucket && topSubBucket?._key !== topField) {
      const temp = topSubBucket;
      topSubBucket = bottomSubBucket;
      bottomSubBucket = temp;
    }
    topFieldValueLabel = topSubBucket?._label;
    bottomFieldValueLabel = bottomSubBucket?._label;
  }

  if (topFieldValueLabel && bottomFieldValueLabel) {
    displayTitle += ` (${topFieldValueLabel} ${i18n.t("vs")} ${bottomFieldValueLabel})`;
  } else if (topFieldValueLabel) {
    displayTitle += ` : ${topFieldValueLabel}`;
  } else if (props.subAggOpLabel) {
    displayTitle += ` : ${i18n.t(props.subAggOpLabel)}`;
  }

  const content = useMemo(() => {
    const buckets = apiResponseBody?.time_of_day_of_week?._buckets || [];

    let dayOfWeekLabels: string[] = [];

    const rows = buckets.map((bucket: TimeOfDayOfWeekRow) => {
      dayOfWeekLabels.push(bucket._label.split(" ")[0]);
      return getTimeOfDayOfWeekRowContent(bucket, settingsState, topField);
    }) || [];

    dayOfWeekLabels = [...new Set(dayOfWeekLabels.filter((label: string) => label !== undefined))];

    const values = rows.map((row: { value: number }) => row.value);

    let maxValue = Math.max(...values);
    let minValue = Math.min(...values);

    if (settingsState.keywordDisplayType === KeywordDisplayType.PERCENTAGE ||
      settingsState.keywordDisplayType === KeywordDisplayType.NET) {
        minValue = 0;
        maxValue = 100;
      }

    const contentRows = rows.map((row: TimeOfDayOfWeekContentRow) => {
      const portionOfRange = (row.value || 0 - minValue) / (maxValue - minValue);
      let color = colord(settingsState.minColor || "rgba(255, 255, 255, 0)").mix(settingsState.maxColor || theme.palette.primary.main, portionOfRange).toRgbString()
      if (settingsState.keywordDisplayType === KeywordDisplayType.NET) {
        if (row.value >= 50) {
          color = colord(settingsState.midColor || theme.palette.primary.main).mix(settingsState.maxColor || theme.palette.primary.dark, (row.value - 50) / 50).toRgbString();
        } else {
          color = colord(settingsState.midColor || theme.palette.primary.main).mix(settingsState.minColor || "rgba(255, 255, 255, 0)", (50 - row.value) / 50).toRgbString();
        }
      }
      return {
        ...row,
        color: color
      };
    });

    return {
      maxValue: maxValue,
      minValue: minValue,
      dayOfWeekLabels: dayOfWeekLabels,
      rows: contentRows
    };
  }, [apiResponseBody, topField, settingsState, settingsState.minColor, settingsState.midColor, settingsState.maxColor, theme]);

  const smallBreakpoint = "375px",
        mediumBreakpoint = "600px",
        largeBreakpoint = "1100px";


        // topFieldValueLabel, bottomFieldValueLabel, displayTitle
  return (
<WidgetLayout header={<>
  <WidgetTitle
    alignment={widget?.data?.settings?.widgetTitleAlignment}
    customTitle={widget?.data?.settings?.customWidgetTitle}
    defaultTitle={displayTitle}
    settingsTitle={widget?.data?.settings?.widgetTitle}
    widgetQueryTitle={queryDisplayTitle} />
  </>} >
  <Box sx={{ height: "100%", width: "100%", position: 'relative' }}>
  <Tooltip
    title={<TimeOfDayOfWeekTooltip
              hoveredIndex={hoveredIndex}
              content={content}
              dateFieldLabel={dateFieldLabel}
              displayTitle={displayTitle}
              settingsState={settingsState}
              topField={topField}
              topFieldValueLabel={topFieldValueLabel}
              bottomFieldValueLabel={bottomFieldValueLabel}
              fieldLabel={props.fieldLabel}
            />}
    open={hoveredIndex !== undefined}
    followCursor={true} >
    <Box sx={{
      position: 'relative',
      height: "100%", width: "100%",
      display: 'grid',
      gridTemplateColumns: settingsState.hideDayLabels ? '1fr' : '50px auto',
      gridTemplateRows: settingsState.hideHourLabels ? 'auto' : 'auto 1fr',
      containerType: 'size',
      containerName: 'timeofdayofweek-hours' }}>
      {!settingsState.hideHourLabels && !settingsState.hideDayLabels && (
      <Box sx={{
        display: (settingsState.hideHourLabels || settingsState.hideDayLabels) ? 'none' : 'block'
      }}> &nbsp; </Box>
      )}
      <Box sx={{
        display: settingsState.hideHourLabels ? 'none' : 'grid',
        [`@container timeofdayofweek-hours (width < ${smallBreakpoint})`]: {
          gridTemplateColumns: 'repeat(4, 1fr)',
        },
        [`@container timeofdayofweek-hours (width >= ${smallBreakpoint}) and (width < ${mediumBreakpoint})`]: {
          gridTemplateColumns: 'repeat(8, 1fr)',
        },
        [`@container timeofdayofweek-hours (width >= ${mediumBreakpoint}) and (width < ${largeBreakpoint})`]: {
          gridTemplateColumns: 'repeat(12, 1fr)',
        },
        [`@container timeofdayofweek-hours (width >= ${largeBreakpoint})`]: {
          gridTemplateColumns: 'repeat(24, 1fr)',
        },
      }}>
        {hoursOfDay.map((hour: number) => (
          <Box key={hour} sx={{
            textAlign: 'left',
            display: 'none',
            [`@container timeofdayofweek-hours (width < ${smallBreakpoint})`]: {
              ":nth-of-type(6n + 1)": {
                display: 'grid'
              }
            },
            [`@container timeofdayofweek-hours (width >= ${smallBreakpoint}) and (width < ${mediumBreakpoint})`]: {
              ":nth-of-type(3n + 1)": {
                display: 'block'
              }
            },
            [`@container timeofdayofweek-hours (width >= ${mediumBreakpoint}) and (width < ${largeBreakpoint})`]: {
              ":nth-of-type(2n + 1)": {
                display: 'block'
              }
            },
            [`@container timeofdayofweek-hours (width >= ${largeBreakpoint})`]: {
              display: 'block'
            },
            }}>
            <Typography variant='caption'>{hour}:00</Typography>
          </Box>
        ))}
      </Box>
      {!settingsState.hideDayLabels && (
      <Box sx={{
        height: "100%", width: "100%",
        display: settingsState.hideDayLabels ? 'none' : 'grid',
        gridTemplateRows: 'repeat(7, 1fr)',
        gridTemplateColumns: '1fr',
        ["@container timeofdayofweek-hours (height < 150px)"]: {
            gridTemplateRows: 'repeat(4, 1fr)',
        }}}>
        {content.dayOfWeekLabels.map((label: string, index: number) => (
          <Box key={index} sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            p:0, m:0, paddingRight: '5px',
            ["@container timeofdayofweek-hours (height < 150px)"]: {
              display: 'none',
              ":nth-of-type(2n + 1)": {
                display: 'flex'
              }
            }}}>
            <Typography variant='caption' sx={{ lineHeight: "1em", display:"block" }}>{label}</Typography>
          </Box>
        ))}
      </Box>
      )}
      <Box sx={{
        gridColumnStart: settingsState.hideDayLabels ? 1 : 2,
        gridColumnEnd: 3,
        position: 'relative',
        cursor: 'pointer'}}>
        <Box
          sx={{
            height: "100%", width: "100%",
            display: 'grid',
            gridTemplateColumns: 'repeat(24, 1fr)',
            gridTemplateRows: 'repeat(7, 1fr)',
            cursor: 'pointer'}}
          onMouseLeave={() => { setHoveredIndex(undefined); }} >
          {content.rows.map((bucket: any, index: number) => (
            <Box key={index}
              sx={{ backgroundColor: bucket.color, cursor: 'pointer' }}
              onMouseEnter={() => { setHoveredIndex(index); }} >
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </Tooltip>
  </Box>
</WidgetLayout>
  )
}
